/**
 * ScrollRestore —— 无限滚动状态恢复组件
 *
 * 在用户离开列表页（如点击文章详情）前，将已加载的 DOM 快照和滚动位置
 * 保存到 sessionStorage；浏览器返回时从快照恢复，避免重新从第 1 页加载。
 *
 * 时序设计要点：
 * - **save 不挂在 destructor 上**。Unpoly swap 顺序为
 *   `beforeRemove(=destroy) → replaceWith → afterInsert(=compile)`
 *   （见 packages/unpoly/src/unpoly/motion.js），destroy 时新容器尚未入 DOM，
 *   无法可靠区分"原地替换"与"导航离开"。曾导致排序切换被旧快照立即覆盖。
 * - save 由两个信号触发：
 *   1. `up:fragment:loaded`：Unpoly 已拿到响应、即将 swap，可读 renderOptions.target
 *      判断本容器是被原地替换还是被祖先一起换掉。仅后者需要 save。
 *   2. `pagehide`：浏览器跳转外站、刷新或关闭 tab 时兜底。
 * - locationKey 含 `pathname + search`，避免同 pathname 不同 query（排序）
 *   的 key 冲突。
 */

const STORAGE_PREFIX = "scroll-restore:";

interface Snapshot {
  /** 列表容器的 innerHTML */
  html: string;
  /** 滚动位置 */
  scrollTop: number;
  /** 保存时间戳，用于过期清理 */
  timestamp: number;
}

/** 快照最大有效期：10 分钟 */
const MAX_AGE_MS = 10 * 60 * 1000;

/**
 * 低于此 scrollTop（且未做过 loadmore）的状态不值得 save——服务端首屏渲染
 * 已经一致，反而徒增 storage 压力。
 */
const TRIVIAL_SCROLL_THRESHOLD_PX = 100;

function storageKey(locationKey: string): string {
  return STORAGE_PREFIX + locationKey;
}

/**
 * 影响列表内容的查询参数白名单。其它参数（utm_*、ref、fbclid、gclid 等追踪参数）
 * 不参与 storage key 计算，避免外链进站后命中不上快照。
 *
 * 维护规则：只加"会改变服务端返回内容"的参数。新增筛选维度时同步加入。
 */
const RELEVANT_QUERY_KEYS: ReadonlySet<string> = new Set([
  "sort",
  "order",
  "keyword",
  "q",
]);

/**
 * 生成与当前 URL 对应的稳定 key：pathname + 排序后的白名单 query。
 * - 排序使 `?sort=a&order=b` 与 `?order=b&sort=a` 命中同一快照。
 * - 过滤白名单避免追踪参数污染。
 */
function currentLocationKey(): string {
  const params = new URLSearchParams(window.location.search);
  const relevantPairs: Array<[string, string]> = [];
  for (const [k, v] of params) {
    if (RELEVANT_QUERY_KEYS.has(k)) relevantPairs.push([k, v]);
  }
  relevantPairs.sort(([a, av], [b, bv]) =>
    a === b ? av.localeCompare(bv) : a.localeCompare(b),
  );

  const filtered = new URLSearchParams();
  for (const [k, v] of relevantPairs) filtered.append(k, v);
  const search = filtered.toString();
  return window.location.pathname + (search ? `?${search}` : "");
}

/**
 * 尝试写入 sessionStorage。区分"配额超限"和其它错误：
 * - QuotaExceededError 返回 false，调用方可走淘汰流程。
 * - 其它异常（比如 sessionStorage 在 Safari 隐私模式下整体不可用）返回 false 静默吞掉，
 *   不抛出影响主流程。
 */
function trySetItem(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * 找出同前缀里时间戳最旧的一条并删除（排除 keepKey）。返回是否真的删除了。
 *
 * 损坏（JSON 解析失败）的条目优先淘汰，记为 timestamp = 0。
 */
function evictOldestSnapshot(keepKey: string): boolean {
  let oldestKey: string | null = null;
  let oldestTimestamp = Infinity;

  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX) || k === keepKey) continue;
    let ts = 0;
    try {
      const raw = sessionStorage.getItem(k);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<Snapshot>;
        ts = typeof parsed.timestamp === "number" ? parsed.timestamp : 0;
      }
    } catch {
      ts = 0; // 损坏条目优先淘汰
    }
    if (ts < oldestTimestamp) {
      oldestTimestamp = ts;
      oldestKey = k;
    }
  }

  if (oldestKey) {
    sessionStorage.removeItem(oldestKey);
    return true;
  }
  return false;
}

/**
 * 把 Unpoly target 规范成选择器数组。Target 可能是字符串、逗号分隔字符串或字符串数组，
 * 也可能含 `:after` / `:before` / `:main` 等伪类，这里只取选择器主体。
 */
function parseTargets(target: unknown): string[] {
  if (!target) return [];
  const list = Array.isArray(target) ? target : [String(target)];
  return list
    .flatMap((s) => String(s).split(","))
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.replace(/:(?:after|before|main)\b/g, ""))
    .filter(Boolean);
}

export class ScrollRestore {
  /** 全局开关：false 时跳过所有 save 和 restore 逻辑。默认启用。 */
  static enabled = true;

  private container: HTMLElement;
  /** 容器构造时的 location（pathname + search），save/restore 都基于此值 */
  private locationKey: string;
  private lastScrollTop = 0;
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Unpoly up.on 的取消订阅函数 */
  private unbindFragmentLoaded: (() => void) | null = null;
  /** 容器初始 innerHTML 长度，用于判断是否发生过 loadmore */
  private initialHtmlLength: number;

  constructor(container: HTMLElement) {
    this.container = container;
    this.locationKey = currentLocationKey();
    this.initialHtmlLength = container.innerHTML.length;

    if (this.tryRestore()) return;

    this.trackScroll();
    this.bindNavigationListeners();
  }

  private tryRestore(): boolean {
    if (!ScrollRestore.enabled) return false;
    const key = storageKey(this.locationKey);
    const snapshot = this.load(key);
    if (!snapshot) return false;

    if (Date.now() - snapshot.timestamp > MAX_AGE_MS) {
      sessionStorage.removeItem(key);
      return false;
    }

    console.info("[ScrollRestore] 检测到快照，正在恢复...", this.locationKey);

    // 1) 先填充 DOM。
    this.container.innerHTML = snapshot.html;

    // 2) **先同步恢复滚动位置，再编译子树**——顺序关键。
    //    若先 up.hello，恢复出来的 .infinite-sentinel 在 scrollTop=0 时往往落在
    //    视口内，IntersectionObserver 会在用户还没动之前就抢先加载下一页。
    //    读一次 scrollHeight 强制布局，避免 scrollTop 被 clamp。
    const scrollElement = (document.scrollingElement ||
      document.documentElement) as HTMLElement;
    void scrollElement.scrollHeight;
    scrollElement.scrollTop = snapshot.scrollTop;

    // 3) 再编译，此时 sentinel 已在视口外，不会误触发。
    up.hello(this.container);

    // 4) 图片/字体晚加载会让 scrollHeight 持续变化，最初的 scrollTop 可能被 clamp。
    //    用 ResizeObserver 跟踪容器尺寸，每次变化都重新校正，直至：
    //      a. 用户主动操作（wheel/touchstart/keydown）
    //      b. 超过 RESTORE_TIMEOUT_MS（兜底）
    this.stabilizeScroll(scrollElement, snapshot.scrollTop);

    sessionStorage.removeItem(key);

    this.lastScrollTop = snapshot.scrollTop;
    this.trackScroll();
    this.bindNavigationListeners();
    return true;
  }

  /**
   * 持续校正 scrollTop 直到布局稳定或用户介入。
   * 不和用户抢控制权——任何主动滚动输入立刻停止。
   */
  private stabilizeScroll(scrollElement: HTMLElement, targetTop: number): void {
    const RESTORE_TIMEOUT_MS = 2000;

    let stopped = false;
    const stop = (): void => {
      if (stopped) return;
      stopped = true;
      observer.disconnect();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
      clearTimeout(timer);
      console.info("[ScrollRestore] 滚动位置已恢复:", targetTop);
    };

    const observer = new ResizeObserver(() => {
      if (stopped) return;
      if (Math.abs(scrollElement.scrollTop - targetTop) > 4) {
        scrollElement.scrollTop = targetTop;
      }
    });
    observer.observe(this.container);

    // 用户介入立即停止——优先用 once 减少手动解绑负担
    window.addEventListener("wheel", stop, { passive: true, once: true });
    window.addEventListener("touchstart", stop, { passive: true, once: true });
    window.addEventListener("keydown", stop, { once: true });

    const timer = setTimeout(stop, RESTORE_TIMEOUT_MS);
  }

  private trackScroll(): void {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  private handleScroll = (): void => {
    if (this.scrollTimer) return;
    this.scrollTimer = setTimeout(() => {
      const scrollElement =
        document.scrollingElement || document.documentElement;
      this.lastScrollTop = scrollElement.scrollTop;
      this.scrollTimer = null;
    }, 150);
  };

  /** 注册"导航离开"的 save 触发器。 */
  private bindNavigationListeners(): void {
    this.unbindFragmentLoaded = up.on(
      "up:fragment:loaded",
      this.handleFragmentLoaded,
    );
    window.addEventListener("pagehide", this.handlePageHide);
  }

  /**
   * Unpoly 已拿到响应，即将 swap 片段。此时容器仍在 DOM 中，可安全读 innerHTML。
   *
   * 决策：
   * - target 命中本容器自身 → 原地替换（如排序切换），不 save。
   * - target 命中本容器祖先 → 本容器即将随祖先被丢弃，立即 save。
   * - 其余无关 swap → 忽略。
   */
  private handleFragmentLoaded = (event: up.FragmentLoadedEvent): void => {
    const targets = parseTargets(event.renderOptions?.target);
    if (targets.length === 0) return;

    for (const sel of targets) {
      let el: Element | null = null;
      try {
        el = document.querySelector(sel);
      } catch {
        continue;
      }
      if (!el) continue;

      if (el === this.container) {
        // 原地替换：不 save。新容器构造时会用新的 locationKey，不会误恢复。
        return;
      }
      if (el.contains(this.container)) {
        // 祖先被替换，本容器即将丢失。
        this.save();
        return;
      }
    }
  };

  /**
   * 浏览器卸载（关闭 tab、刷新、跳转外站等）：兜底 save。
   *
   * 进入 BFCache（`event.persisted === true`）时不算真正离开——页面会原样
   * 从内存恢复，DOM/JS 状态都在，无需快照。还能省一次序列化与 storage 写入。
   */
  private handlePageHide = (e: PageTransitionEvent): void => {
    if (e.persisted) return;
    this.save();
  };

  /**
   * 当前状态是否值得 save。
   *
   * - 用户没怎么滚（scrollTop < 阈值）且没触发过 loadmore（innerHTML 未显著膨胀）→
   *   服务端首屏渲染已与"快照恢复"等价，没必要写 storage。
   * - 反之（滚过 / 加载过更多页）→ 必须 save，否则用户回来会丢状态。
   */
  private isTrivialSave(): boolean {
    if (this.lastScrollTop >= TRIVIAL_SCROLL_THRESHOLD_PX) return false;
    // 1.2 倍是经验阈值：单条新增文章卡片大致对应 +几 KB，足以越过。
    if (this.container.innerHTML.length > this.initialHtmlLength * 1.2)
      return false;
    return true;
  }

  private save(): void {
    if (!ScrollRestore.enabled) return;
    if (this.isTrivialSave()) return;

    const snapshot: Snapshot = {
      html: this.container.innerHTML,
      scrollTop: this.lastScrollTop,
      timestamp: Date.now(),
    };

    const key = storageKey(this.locationKey);
    const json = JSON.stringify(snapshot);

    // 容量不足时按时间戳从老到新淘汰同前缀快照后重试。
    // 单条快照可能 100KB+，sessionStorage 上限 ~5MB，热点页很容易撞到。
    let evicted = 0;
    while (true) {
      if (trySetItem(key, json)) {
        if (evicted > 0) {
          console.info(
            `[ScrollRestore] 容量不足，淘汰 ${evicted} 个旧快照后保存成功, key:`,
            key,
          );
        } else {
          console.info(
            "[ScrollRestore] 快照已保存, key:",
            key,
            "scrollTop:",
            snapshot.scrollTop,
          );
        }
        return;
      }
      // setItem 失败：尝试淘汰一个最老的（不含本 key 自身）。
      if (!evictOldestSnapshot(key)) break;
      evicted++;
      // 防御性上限：避免极端情况下无限循环。
      if (evicted > 32) break;
    }
    console.warn(
      "[ScrollRestore] 保存快照失败：sessionStorage 容量不足或不可用 (已淘汰",
      evicted,
      "条仍未成功)",
    );
  }

  private load(key: string): Snapshot | null {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as Snapshot;
    } catch {
      return null;
    }
  }

  /** 清除所有 scroll-restore 快照（登入/登出场景）。 */
  static clear(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
    }
    if (keysToRemove.length > 0) {
      console.info(
        "[ScrollRestore] 已清除所有快照, 数量:",
        keysToRemove.length,
      );
    }
  }

  /**
   * 销毁组件：仅解绑监听器。**不在此 save**——save 已由
   * `up:fragment:loaded` / `pagehide` 在更可靠的时机触发。
   */
  destroy(): void {
    window.removeEventListener("scroll", this.handleScroll);
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
    this.unbindFragmentLoaded?.();
    this.unbindFragmentLoaded = null;
    window.removeEventListener("pagehide", this.handlePageHide);
  }

  static compiler(selector: string): void {
    up.compiler(selector, (element: Element) => {
      const restore = new ScrollRestore(element as HTMLElement);
      return () => restore.destroy();
    });

    up.on("auth:signin auth:signout", () => {
      console.info("[ScrollRestore] auth:signin auth:signout 已清除旧快照");
      ScrollRestore.clear();
    });
  }
}
